/**
 * Track0tic Control Portal - Cold Chain Monitoring
 * Live Map + Temperature + Humidity Dashboard
 */
import { useState, useEffect, useCallback } from 'react';
import { controlApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useControlTheme } from '@/contexts/ControlThemeContext';
import { cn } from '@/lib/utils';
import {
  Thermometer, Droplets, Map, AlertTriangle, CheckCircle,
  RefreshCw, Loader2, Wifi, WifiOff, Battery, TrendingUp,
  Download, Filter, MapPin
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Area, ComposedChart, Legend, ReferenceLine
} from 'recharts';

export default function ColdChainPage() {
  const { isDark } = useControlTheme();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('live-map');
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('all');
  const [timeRange, setTimeRange] = useState(24);
  
  // Data states
  const [liveMapData, setLiveMapData] = useState({ shipments: [], stats: {} });
  const [devices, setDevices] = useState([]);
  const [trends, setTrends] = useState([]);
  const [dashboard, setDashboard] = useState({});

  // Theme colors
  const colors = isDark ? {
    bgCard: 'bg-slate-800',
    border: 'border-slate-700',
    textPrimary: 'text-slate-100',
    textSecondary: 'text-blue-400',
    textMuted: 'text-slate-400',
  } : {
    bgCard: 'bg-white',
    border: 'border-slate-200',
    textPrimary: 'text-slate-900',
    textSecondary: 'text-blue-600',
    textMuted: 'text-slate-500',
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = selectedTenant !== 'all' ? { tenant_id: selectedTenant } : {};
      
      const [tenantsRes, liveMapRes, devicesRes, trendsRes, dashboardRes] = await Promise.all([
        controlApi.getTenants(),
        controlApi.getColdChainLiveMap(params),
        controlApi.getColdChainDevices(params),
        controlApi.getColdChainTrends({ ...params, hours: timeRange }),
        controlApi.getColdChainDashboard(params)
      ]);
      
      setTenants(tenantsRes.data || []);
      setLiveMapData(liveMapRes.data || { shipments: [], stats: {} });
      setDevices(devicesRes.data || []);
      setTrends(trendsRes.data?.data || []);
      setDashboard(dashboardRes.data || {});
    } catch (error) {
      toast.error('Failed to load cold chain data');
    } finally {
      setLoading(false);
    }
     
  }, [selectedTenant, timeRange]);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
     
  }, [fetchData]);

  // Stat Card Component
  const StatCard = ({ icon: Icon, label, value, status, color = 'blue' }) => {
    const colorClasses = {
      blue: 'bg-blue-500/10 text-blue-500',
      green: 'bg-emerald-500/10 text-emerald-500',
      amber: 'bg-amber-500/10 text-amber-500',
      red: 'bg-red-500/10 text-red-500',
    };
    return (
      <div className={cn("rounded-lg p-4 border", colors.bgCard, colors.border)}>
        <div className="flex items-center justify-between">
          <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", colorClasses[color])}>
            <Icon className="w-5 h-5" />
          </div>
          {status && (
            <Badge variant={status === 'normal' ? 'outline' : 'destructive'} className="text-xs">
              {status}
            </Badge>
          )}
        </div>
        <p className={cn("text-2xl font-bold mt-3", colors.textPrimary)}>{value}</p>
        <p className={cn("text-sm", colors.textMuted)}>{label}</p>
      </div>
    );
  };

  // Status indicator for temperature/humidity
  const getStatusColor = (status) => {
    switch (status) {
      case 'normal': return 'text-emerald-500';
      case 'warning': return 'text-amber-500';
      case 'breach': return 'text-red-500';
      default: return colors.textMuted;
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'normal': return 'bg-emerald-500/10';
      case 'warning': return 'bg-amber-500/10';
      case 'breach': return 'bg-red-500/10';
      default: return 'bg-slate-500/10';
    }
  };

  if (loading && devices.length === 0) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
        <div className="text-center">
          <Loader2 className={cn("w-10 h-10 animate-spin mx-auto mb-4", colors.textSecondary)} />
          <p className={colors.textMuted}>Loading cold chain data...</p>
        </div>
      </div>
    );
  }

  const stats = liveMapData.stats || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className={cn("text-2xl font-bold", colors.textPrimary)}>Cold Chain Monitoring</h1>
          <p className={colors.textMuted}>Real-time temperature and humidity tracking</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedTenant} onValueChange={setSelectedTenant}>
            <SelectTrigger className={cn("w-48", colors.border)}>
              <SelectValue placeholder="All Customers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Customers</SelectItem>
              {tenants.map(t => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            onClick={fetchData}
            disabled={loading}
            className={colors.border}
          >
            <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <StatCard 
          icon={Map} 
          label="Active Shipments" 
          value={stats.total_active || 0} 
          color="blue" 
        />
        <StatCard 
          icon={Thermometer} 
          label="Temp Normal" 
          value={stats.temp_normal || 0} 
          color="green" 
        />
        <StatCard 
          icon={Thermometer} 
          label="Temp Warning" 
          value={stats.temp_warning || 0} 
          color="amber" 
        />
        <StatCard 
          icon={Thermometer} 
          label="Temp Breach" 
          value={stats.temp_breach || 0} 
          color="red" 
        />
        <StatCard 
          icon={Droplets} 
          label="Humidity OK" 
          value={(stats.humidity_normal || 0)} 
          color="green" 
        />
        <StatCard 
          icon={Droplets} 
          label="Humidity Alert" 
          value={(stats.humidity_warning || 0) + (stats.humidity_breach || 0)} 
          color="amber" 
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={cn("grid w-full grid-cols-4", isDark ? "bg-slate-800" : "bg-slate-100")}>
          <TabsTrigger value="live-map" className="gap-2">
            <Map className="w-4 h-4" />
            Live Map
          </TabsTrigger>
          <TabsTrigger value="devices" className="gap-2">
            <Thermometer className="w-4 h-4" />
            Devices
          </TabsTrigger>
          <TabsTrigger value="trends" className="gap-2">
            <TrendingUp className="w-4 h-4" />
            Trends
          </TabsTrigger>
          <TabsTrigger value="alerts" className="gap-2">
            <AlertTriangle className="w-4 h-4" />
            Excursions
          </TabsTrigger>
        </TabsList>

        {/* Live Map Tab */}
        <TabsContent value="live-map" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Map Placeholder */}
            <div className={cn("lg:col-span-2 rounded-lg border p-4 h-[500px]", colors.bgCard, colors.border)}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={cn("font-semibold", colors.textPrimary)}>Live Shipment Map</h3>
                <Badge variant="outline" className="gap-1">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  Live
                </Badge>
              </div>
              <div className={cn("h-[420px] rounded-lg flex items-center justify-center", isDark ? "bg-slate-700" : "bg-slate-100")}>
                <div className="text-center">
                  <MapPin className={cn("w-12 h-12 mx-auto mb-4", colors.textMuted)} />
                  <p className={colors.textMuted}>Map integration coming soon</p>
                  <p className={cn("text-sm", colors.textMuted)}>{stats.total_active || 0} active shipments</p>
                </div>
              </div>
            </div>

            {/* Shipment List */}
            <div className={cn("rounded-lg border p-4 h-[500px] overflow-y-auto", colors.bgCard, colors.border)}>
              <h3 className={cn("font-semibold mb-4", colors.textPrimary)}>Active Shipments</h3>
              <div className="space-y-3">
                {liveMapData.shipments?.length === 0 ? (
                  <p className={cn("text-center py-8", colors.textMuted)}>No active shipments</p>
                ) : (
                  liveMapData.shipments?.map((shipment, idx) => (
                    <div 
                      key={shipment.trip_id || idx}
                      className={cn("p-3 rounded-lg border", isDark ? "bg-slate-700/50 border-slate-600" : "bg-slate-50 border-slate-200")}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className={cn("font-medium text-sm", colors.textPrimary)}>{shipment.trip_number}</span>
                        {shipment.is_online ? (
                          <Wifi className="w-4 h-4 text-green-500" />
                        ) : (
                          <WifiOff className="w-4 h-4 text-red-500" />
                        )}
                      </div>
                      <p className={cn("text-xs mb-2", colors.textMuted)}>{shipment.tenant_name}</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div className={cn("px-2 py-1 rounded text-center", getStatusBg(shipment.temperature?.status))}>
                          <span className={cn("text-xs", getStatusColor(shipment.temperature?.status))}>
                            {shipment.temperature?.value !== null ? `${shipment.temperature.value}°C` : 'N/A'}
                          </span>
                        </div>
                        <div className={cn("px-2 py-1 rounded text-center", getStatusBg(shipment.humidity?.status))}>
                          <span className={cn("text-xs", getStatusColor(shipment.humidity?.status))}>
                            {shipment.humidity?.value !== null ? `${shipment.humidity.value}%` : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Devices Tab */}
        <TabsContent value="devices" className="mt-6">
          <div className={cn("rounded-lg border", colors.bgCard, colors.border)}>
            <div className="p-4 border-b border-inherit">
              <h3 className={cn("font-semibold", colors.textPrimary)}>Device Cold Chain Status</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={cn(isDark ? "bg-slate-700/50" : "bg-slate-50")}>
                  <tr>
                    <th className={cn("px-4 py-3 text-left text-xs font-medium uppercase", colors.textMuted)}>Device</th>
                    <th className={cn("px-4 py-3 text-left text-xs font-medium uppercase", colors.textMuted)}>Customer</th>
                    <th className={cn("px-4 py-3 text-left text-xs font-medium uppercase", colors.textMuted)}>Temperature</th>
                    <th className={cn("px-4 py-3 text-left text-xs font-medium uppercase", colors.textMuted)}>Humidity</th>
                    <th className={cn("px-4 py-3 text-left text-xs font-medium uppercase", colors.textMuted)}>Battery</th>
                    <th className={cn("px-4 py-3 text-left text-xs font-medium uppercase", colors.textMuted)}>Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-inherit">
                  {devices.map((device, idx) => (
                    <tr key={device.id || idx} className={cn(isDark ? "hover:bg-slate-700/30" : "hover:bg-slate-50")}>
                      <td className="px-4 py-3">
                        <span className={cn("font-medium", colors.textPrimary)}>{device.device_uid}</span>
                        {device.active_trip && (
                          <p className={cn("text-xs", colors.textMuted)}>Trip: {device.active_trip}</p>
                        )}
                      </td>
                      <td className={cn("px-4 py-3 text-sm", colors.textMuted)}>{device.tenant_name || '-'}</td>
                      <td className="px-4 py-3">
                        <div className={cn("inline-flex items-center gap-2 px-2 py-1 rounded", getStatusBg(device.temp_status))}>
                          <Thermometer className={cn("w-4 h-4", getStatusColor(device.temp_status))} />
                          <span className={cn("text-sm font-medium", getStatusColor(device.temp_status))}>
                            {device.current_temp !== null ? `${device.current_temp}°C` : 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className={cn("inline-flex items-center gap-2 px-2 py-1 rounded", getStatusBg(device.humidity_status))}>
                          <Droplets className={cn("w-4 h-4", getStatusColor(device.humidity_status))} />
                          <span className={cn("text-sm font-medium", getStatusColor(device.humidity_status))}>
                            {device.current_humidity !== null ? `${device.current_humidity}%` : 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Battery className={cn("w-4 h-4", device.battery_level > 20 ? 'text-green-500' : 'text-red-500')} />
                          <span className={colors.textMuted}>{device.battery_level || 0}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {device.is_online ? (
                          <Badge className="bg-green-500/10 text-green-500 border-green-500/30">Online</Badge>
                        ) : (
                          <Badge variant="destructive">Offline</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {devices.length === 0 && (
                <p className={cn("text-center py-8", colors.textMuted)}>No devices found</p>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Temperature Trends */}
            <div className={cn("rounded-lg border p-4", colors.bgCard, colors.border)}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={cn("font-semibold", colors.textPrimary)}>Temperature Trends</h3>
                <Select value={timeRange.toString()} onValueChange={(v) => setTimeRange(parseInt(v))}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6">Last 6 hrs</SelectItem>
                    <SelectItem value="24">Last 24 hrs</SelectItem>
                    <SelectItem value="72">Last 3 days</SelectItem>
                    <SelectItem value="168">Last 7 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={trends}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e2e8f0'} />
                    <XAxis 
                      dataKey="timestamp" 
                      tickFormatter={(v) => new Date(v).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      stroke={isDark ? '#94a3b8' : '#64748b'}
                    />
                    <YAxis 
                      domain={[0, 15]} 
                      stroke={isDark ? '#94a3b8' : '#64748b'}
                      label={{ value: '°C', position: 'insideLeft', angle: -90 }}
                    />
                    <Tooltip 
                      contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: 'none', borderRadius: '8px' }}
                      labelFormatter={(v) => new Date(v).toLocaleString()}
                    />
                    <Legend />
                    <ReferenceLine y={2} stroke="#f59e0b" strokeDasharray="5 5" label="Min" />
                    <ReferenceLine y={8} stroke="#f59e0b" strokeDasharray="5 5" label="Max" />
                    <Area 
                      type="monotone" 
                      dataKey="max_temp" 
                      fill="#ef444420" 
                      stroke="#ef4444"
                      name="Max Temp"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="avg_temp" 
                      stroke="#3b82f6" 
                      strokeWidth={2}
                      dot={false}
                      name="Avg Temp"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="min_temp" 
                      fill="#22c55e20" 
                      stroke="#22c55e"
                      name="Min Temp"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Humidity Trends */}
            <div className={cn("rounded-lg border p-4", colors.bgCard, colors.border)}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={cn("font-semibold", colors.textPrimary)}>Humidity Trends</h3>
                <Droplets className={cn("w-5 h-5", colors.textSecondary)} />
              </div>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={trends}>
                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#334155' : '#e2e8f0'} />
                    <XAxis 
                      dataKey="timestamp" 
                      tickFormatter={(v) => new Date(v).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      stroke={isDark ? '#94a3b8' : '#64748b'}
                    />
                    <YAxis 
                      domain={[0, 100]} 
                      stroke={isDark ? '#94a3b8' : '#64748b'}
                      label={{ value: '%', position: 'insideLeft', angle: -90 }}
                    />
                    <Tooltip 
                      contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: 'none', borderRadius: '8px' }}
                      labelFormatter={(v) => new Date(v).toLocaleString()}
                    />
                    <Legend />
                    <ReferenceLine y={35} stroke="#f59e0b" strokeDasharray="5 5" label="Min" />
                    <ReferenceLine y={65} stroke="#f59e0b" strokeDasharray="5 5" label="Max" />
                    <Area 
                      type="monotone" 
                      dataKey="max_humidity" 
                      fill="#8b5cf620" 
                      stroke="#8b5cf6"
                      name="Max Humidity"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="avg_humidity" 
                      stroke="#06b6d4" 
                      strokeWidth={2}
                      dot={false}
                      name="Avg Humidity"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="min_humidity" 
                      fill="#14b8a620" 
                      stroke="#14b8a6"
                      name="Min Humidity"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Excursions Tab */}
        <TabsContent value="alerts" className="mt-6">
          <div className={cn("rounded-lg border p-6 text-center", colors.bgCard, colors.border)}>
            <AlertTriangle className={cn("w-12 h-12 mx-auto mb-4", colors.textMuted)} />
            <h3 className={cn("text-lg font-semibold mb-2", colors.textPrimary)}>Temperature & Humidity Excursions</h3>
            <p className={colors.textMuted}>View historical breaches and compliance reports</p>
            <Button className="mt-4 bg-blue-600 hover:bg-blue-700">
              <Download className="w-4 h-4 mr-2" />
              Download Excursion Report
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
